/**
 * PURPOSE: Mostrar la fase de eliminación a pantalla completa y seleccionar el bracket correcto por evento.
 * RESPONSIBILITIES: Mantener instituciones participantes, conservar el flujo legacy y habilitar Bracket2 solo para idevent 17.
 * COLLABORATORS: Regionales, Bracket, Bracket2 y endpoints existentes de configuración/matches/participantes.
 * ROUTE: frontend/src/components/FullScreenModal/FullScreenEliminatoria/FullScreenEliminatoria.jsx
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../../config/config.js";
import { useParams } from "react-router-dom";

import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import { FaWandMagicSparkles } from "react-icons/fa6";

import Regionales from "../FullScreenGrupos/Instituciones/Regionales/Regionales";
import Bracket from "./Bracket/Bracket";
import Bracket2 from "./Bracket/Bracket2";

import shuffle from "lodash.shuffle";

const FullScreenEliminatoria = ({ open, setOpen }) => {
  const [color] = useState("#1e22aa");
  const [configCtg, setConfigCtg] = useState({});
  const [matches, setMatches] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const { idevent, idsport } = useParams();

  const eventId = Number(idevent);
  const sportId = Number(idsport);
  const useEvent17Bracket = eventId === 17;

  /* Flujo legacy: se conserva únicamente para eventos distintos del 17. */
  const assignRandomBracket = async () => {
    if (participantes.length < 12 || matches.length === 0) return;

    const shuffled = shuffle(participantes);
    const updated = matches.map((match) => ({ ...match }));

    const ronda1 = updated
      .filter((match) => match.tag_final?.toLowerCase() === "ronda 1")
      .sort((a, b) => a.idmatch - b.idmatch)
      .slice(0, 6);

    let index = 0;
    ronda1.forEach((match) => {
      match.idparticipant1 = shuffled[index++]?.idparticipant ?? null;
      match.idparticipant2 = shuffled[index++]?.idparticipant ?? null;
    });

    let lastR2 = null;
    if (sportId === 5 && index < shuffled.length) {
      const ronda2 = updated
        .filter((match) => match.tag_final?.toLowerCase() === "ronda 2")
        .sort((a, b) => a.idmatch - b.idmatch);

      if (ronda2.length) {
        lastR2 = ronda2[ronda2.length - 1];
        lastR2.idparticipant1 = shuffled[index].idparticipant;
        lastR2.idparticipant2 = null;
      }
    }

    const payload = [...ronda1, ...(lastR2 ? [lastR2] : [])].map(
      ({ idmatch, idparticipant1, idparticipant2 }) => ({
        idmatch,
        idparticipant1,
        idparticipant2,
      })
    );

    try {
      await axios.patch(`${API_BASE_URL}/patchmatchesparticipants`, payload);
      await getMatches();
    } catch (error) {
      console.error("Error actualizando matches:", error);
    }
  };

  const getMatches = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/getmatches`, {
        idevent,
        idsport,
      });
      setMatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener los matches:", error);
    }
  };

  const getParticipantes = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/getparticipants`, {
        idevent: eventId,
        idsport: sportId,
        idtypephase: 1,
      });
      setParticipantes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!idevent || !idsport) return;

      try {
        const configResponse = await axios.post(
          `${API_BASE_URL}/config_category`,
          {
            idevent: eventId,
            idsport: sportId,
          }
        );

        if (!cancelled) setConfigCtg(configResponse.data || {});

        if (!useEvent17Bracket) {
          await Promise.all([getParticipantes(), getMatches()]);
        }
      } catch (error) {
        console.error("Error cargando fase de eliminación:", error);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [idevent, idsport, eventId, sportId, useEvent17Bracket]);

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <ModalDialog layout="fullscreen">
        <ModalClose sx={{ width: "35px" }} />
        <DialogTitle>Fase de Eliminación</DialogTitle>

        <DialogContent>
          <div
            className="w-full rounded-lg py-3 text-center text-2xl font-bold text-white"
            style={{ backgroundColor: color }}
          >
            <span style={{ fontSize: "2.5rem" }}>
              INSTITUCIONES PARTICIPANTES
            </span>
          </div>

          <Regionales config={configCtg} />

          <div
            className="relative mt-4 w-full rounded-lg py-3 text-center text-2xl font-bold text-white"
            style={{ backgroundColor: color }}
          >
            <span style={{ fontSize: "2.5rem" }}>
              {useEvent17Bracket
                ? "LLAVE DE ELIMINACIÓN"
                : "ELIMINACIÓN SIMPLE"}
            </span>

            {!useEvent17Bracket && (
              <FaWandMagicSparkles
                className="magic-sparkle-icon"
                style={{
                  position: "absolute",
                  right: "25px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "2rem",
                  cursor: "pointer",
                }}
                onClick={assignRandomBracket}
              />
            )}
          </div>

          {useEvent17Bracket ? (
            <Bracket2
              idevent={eventId}
              idsport={sportId}
              moodName={configCtg?.moodName}
            />
          ) : (
            <Bracket matches={matches} participantes={participantes} />
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default FullScreenEliminatoria;
